import React, { ButtonHTMLAttributes, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { KitFlexRow } from './KitFlexbox';
import KitIcon from './KitIcon';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';

export interface KitPillButtonData {
  icon?: string;
  text: string;
  value: string;
}

export interface KitPillButtonGroupProps {
  className?: string;
  'aria-label'?: string;
  buttons: KitPillButtonData[];
  exclusive?: boolean;
  onChange: (values: string[]) => void;
  orientation: 'horizontal' | 'vertical';
  value?: string | string[];
}

interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  icon?: string;
  text: string;
  value: string;
}

const PillButtonGroup = styled.div<{ orientation: string }>`
  ${({ orientation }) =>
    orientation === 'horizontal'
      ? css`
          display: flex;
          flex-direction: row;
          & > * {
            margin-right: ${constants.spacing.absolute.s}px;
          }
          & > *:last-child {
            margin-right: 0;
          }
        `
      : css`
          display: inline-flex;
          flex-direction: column;
          & > * {
            margin-bottom: ${constants.spacing.absolute.s}px;
          }
          & > *:last-child {
            margin-bottom: 0;
          }
        `};
`;

const StyledButton = styled.button<{ active: boolean }>`
  background-color: ${({ active, theme }) => (active ? theme.button.primary.normal.bg : theme.button.secondary.normal.bg)};
  border: none;
  border-radius: 0.9375rem; // 15px
  color: ${({ active, theme }) => (active ? theme.button.primary.normal.text : theme.button.secondary.normal.text)};
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-size: ${constants.fontSize.p}rem;
  font-weight: ${constants.fontWeight.normal};
  height: 1.875rem; // 30px

  &:focus {
    outline-offset: 2px;
    outline: 2px solid ${({ theme }) => theme.focus};
  }
`;

const StyledKitIcon = styled(KitIcon)<{ active: boolean }>`
  fill: ${({ active, theme }) => (active ? theme.button.primary.normal.text : theme.button.secondary.normal.text)};
`;

const StyledKitFlexRow = styled(KitFlexRow)<{ hasIcon: boolean }>`
  align-items: center;
  padding-left: ${({ hasIcon }) => (hasIcon ? 0 : 8)}px;
  padding-right: 8px;
  & > * {
    margin-right: ${constants.spacing.xs}rem;
  }
  & > *:last-child {
    margin-right: 0;
  }
`;

export const KitPillButton: React.FC<PillButtonProps> = ({
  icon,
  text,
  value,
  active, 
  ...rest
}: PillButtonProps) => {
  let iconElement;
  if (icon) {
    iconElement = <StyledKitIcon icon={icon} size={`${constants.iconSize.s}rem`} active={active} />;
  }

  return (
    <StyledButton value={value} active={active} {...rest}>
      <StyledKitFlexRow hasIcon={icon != null}>
        {iconElement}
        <div>{text}</div>
      </StyledKitFlexRow>
    </StyledButton>
  );
};

const KitPillButtonGroup: React.FC<KitPillButtonGroupProps> = ({
  className,
  buttons,
  exclusive,
  onChange,
  orientation,
  value,
  ...rest
}: KitPillButtonGroupProps) => {
  // NOTE: This is just a simple dictionary to allow for quick toggle of button active state
  const [buttonToggleStates, setButtonToggleStates] = useState({});

  useEffect(() => {
    const initialButtonToggleStates = {};
    buttons.forEach((button) => {
      initialButtonToggleStates[button.value] = false;
      if (value) {
        if (typeof value === 'string' && button.value === value) {
          initialButtonToggleStates[button.value] = true;
        } else if (Array.isArray(value)) {
          initialButtonToggleStates[button.value] = value.includes(button.value);
        }
      }
    });
    setButtonToggleStates(initialButtonToggleStates);
  }, [value, buttons]);

  const onClick = (pillButtonValue: string) => {
    const toggleStates = { ...buttonToggleStates };

    if (exclusive) {
      // Only allow toggling of 1 pill at a time.
      Object.entries(toggleStates).forEach(([k]) => {
        if (k === pillButtonValue) {
          toggleStates[pillButtonValue] = !toggleStates[pillButtonValue];
        } else {
          toggleStates[k] = false;
        }
      });
    } else {
      // Allows toggling of multiple pills at a time.
      toggleStates[pillButtonValue] = !toggleStates[pillButtonValue];
    }

    setButtonToggleStates(toggleStates);

    const activeToggleValues = Object.keys(toggleStates).filter((key) => {
      return toggleStates[key] === true; // Only return the items that are toggled ON
    });
    onChange(activeToggleValues);
  };

  const { hasOwnProperty } = Object.prototype;

  const pillButtons: React.JSX.Element[] = [];

  buttons.forEach((button) => {
    const isActive = hasOwnProperty.call(buttonToggleStates, button.value)
      ? buttonToggleStates[button.value]
      : false;

    pillButtons.push(
      <KitPillButton
        key={button.value}
        active={isActive}
        icon={button.icon}
        text={button.text}
        value={button.value}
        onClick={() => onClick(button.value)}
        type='button'
        aria-pressed={isActive}
      />
    );
  });

  return (
    <PillButtonGroup className={className} orientation={orientation} role='group' {...rest}>
      {pillButtons}
    </PillButtonGroup>
  );
};

export default KitPillButtonGroup;
